/**
 * batpacDesign.js — projects and per-kWh results for BatPaC process templates.
 *
 * A BatPaC process template (data/templates/batpac/<cathode>.json, written by
 * scripts/build_batpac_templates.py) carries a design block and a list of
 * configurations: the energy design it ships with, and variants that override
 * some sliders (e.g. a 120 µm cathode) and some constants (BatPaC's power
 * couple has a more porous, carbon-richer anode). The user may also type their
 * own cathode capacity, voltage and density: the block's "material" params.
 *
 * Everything here is synchronous and cheap (a design evaluation plus one LCA
 * and one TEA sum), so the "Design with BatPaC" screen and the design panel
 * recompute on every keystroke.
 */

import { buildProjectFromTemplate } from './templateProject';
import { runTemplateLCA } from './lcaCalculator';
import { runTemplateTEA } from './teaCalculator';
import {
  getDesignParameters,
  resolveParamValues,
  applyDesignParameters,
  DESIGN_PARAMETERS_KEY,
} from '../utils/designParameters';

export const MATERIAL_GROUP = 'material';

/** Keys of a design block's material params (cathode capacity, voltage, density). */
export function materialParamKeys(block) {
  return Object.entries(block?.params || {})
    .filter(([, def]) => def?.group === MATERIAL_GROUP)
    .map(([key]) => key);
}

/**
 * Whether a param value is BatPaC's own (its default) or one the user typed.
 * Values within 1e-9 of the default count as the default.
 */
export function isDefaultValue(def, value) {
  return Number.isFinite(value) && Math.abs(value - def.default) <= 1e-9 * Math.max(1, Math.abs(def.default));
}

/** The template's configurations, energy design first. */
export function templateConfigurations(template) {
  return Array.isArray(template?.batpac?.configurations) ? template.batpac.configurations : [];
}

/**
 * Keep only material values that are numbers inside their slider's range;
 * a blank or out-of-range entry means "use BatPaC's value".
 */
export function cleanMaterialValues(block, values = {}) {
  const out = {};
  for (const key of materialParamKeys(block)) {
    const def = block.params[key];
    const n = Number(values[key]);
    if (values[key] === '' || values[key] == null || !Number.isFinite(n)) continue;
    if (n < def.min || n > def.max) continue;
    out[key] = n;
  }
  return out;
}

/**
 * A project for one configuration of a BatPaC template, with the user's own
 * cathode values applied: the template's design block carrying the
 * configuration's constants, its sliders set to the configuration, and the
 * inventory and Wh/kg recomputed from them.
 *
 * @param {object} template        a loaded BatPaC process template
 * @param {object} [config]        one of templateConfigurations(template); energy if omitted
 * @param {object} [materialValues] { q_pos_mah_g?, ocv_v?, rho_am_pos? }
 * @param {object} [opts]          name / file, passed to buildProjectFromTemplate
 */
export function buildConfigurationProject(template, config = null, materialValues = {}, opts = {}) {
  const base = buildProjectFromTemplate(template, {
    name: opts.name || template.template_name || 'BatPaC cell',
    file: opts.file || null,
    chemistry: template.batpac?.label,
    category: 'BatPaC',
  });
  // The configuration becomes the block's default design, so the design panel's
  // Reset and its "BatPaC" tags refer to the design the user picked.
  const rawBlock = template[DESIGN_PARAMETERS_KEY];
  const overrides = config?.params || {};
  const params = Object.fromEntries(Object.entries(rawBlock?.params || {}).map(([key, def]) => (
    [key, key in overrides ? { ...def, default: overrides[key] } : def])));
  const configured = {
    ...rawBlock,
    params,
    constants: { ...(rawBlock?.constants || {}), ...(config?.constants || {}) },
  };
  const withBlock = { ...base, [DESIGN_PARAMETERS_KEY]: configured };
  const block = getDesignParameters(withBlock);
  if (!block) return withBlock;

  const values = { ...resolveParamValues(block, {}), ...cleanMaterialValues(block, materialValues) };
  const { project } = applyDesignParameters(withBlock, block, values);
  return {
    ...project,
    batpac: { ...(project.batpac || {}), configuration: config?.id || 'energy' },
  };
}

/**
 * The three numbers a configuration card shows: Wh/kg, kg CO2e per kWh and
 * US$ per kWh (materials and energy). Per kg when the project has no energy
 * density, with `perKwh: false`.
 */
export function summarizeProject(project) {
  const lca = runTemplateLCA(project);
  const tea = runTemplateTEA(project);
  if (!lca.success || !tea.success) return null;
  return {
    whPerKg: lca.energy_density_wh_per_kg,
    perKwh: lca.functional_unit_basis === 'energy',
    carbon: lca.totals.climate_change,
    cost: tea.total_cost,
  };
}

/** Percent change of `value` against `baseline`, or null when either is missing. */
export function percentChange(value, baseline) {
  if (!Number.isFinite(value) || !Number.isFinite(baseline) || baseline === 0) return null;
  return ((value - baseline) / Math.abs(baseline)) * 100;
}
